import { ArrowLeft, Check, Hash, MoreHorizontal, Pencil, Send, Trash2, UserPlus, X } from 'lucide-react'
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'

import {
  Avatar,
  Button,
  CommentBody,
  EmptyState,
  ErrorBanner,
  Field,
  Input,
  MentionTextarea,
  Modal,
  Popover,
  PopoverItem,
  Spinner,
  cn,
  formatDay,
  formatMoment,
  useRoster,
} from '@/features/issue'
import type { MemberRow } from '@/features/issue'
import type { PublicUser } from '@/types/api'

import { channelTitle } from './ChannelList'
import { useChatMutations, useMarkRead, useMessages } from './hooks'
import type { ChatChannel, ChatMessage } from './types'

/** The MemberRow shape MentionTextarea expects, built from channel members. */
function toRows(channel: ChatChannel): MemberRow[] {
  return channel.members.map((member) => ({
    organization_id: channel.organization_id,
    user_id: member.user_id,
    role: 'member',
    is_default_org: false,
    joined_at: member.joined_at,
    user: member.user,
  }))
}

export function Conversation({
  slug,
  channel,
  currentUserId,
  isAdmin,
  onBack,
  onLeft,
}: {
  slug: string
  channel: ChatChannel
  currentUserId: number
  isAdmin: boolean
  onBack: () => void
  onLeft: () => void
}) {
  const { messages, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useMessages(
    slug,
    channel.id,
  )
  const { send, edit, remove, rename, addMembers, removeMember, deleteChannel } = useChatMutations(slug)
  const rows = useMemo(() => toRows(channel), [channel])
  const mentioned = useMemo(
    () => channel.members.map((member) => member.user).filter((user): user is PublicUser => Boolean(user)),
    [channel.members],
  )

  const newestId = messages.length ? messages[messages.length - 1]!.id : null
  useMarkRead(slug, channel.id, newestId && newestId > 0 ? newestId : null)

  const [draft, setDraft] = useState('')
  const [editing, setEditing] = useState<ChatMessage | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [dialog, setDialog] = useState<'rename' | 'members' | 'delete' | null>(null)

  useEffect(() => {
    setDraft('')
    setEditing(null)
    setDialog(null)
  }, [channel.id])

  /* ----------------------------------------------------------- scrolling */
  const listRef = useRef<HTMLDivElement>(null)
  const stickToBottom = useRef(true)
  const previousFirstId = useRef<number | null>(null)
  const previousHeight = useRef(0)

  const onScroll = () => {
    const element = listRef.current
    if (!element) return
    const distance = element.scrollHeight - element.scrollTop - element.clientHeight
    stickToBottom.current = distance < 80
  }

  useLayoutEffect(() => {
    const element = listRef.current
    if (!element) return
    const firstId = messages[0]?.id ?? null
    if (previousFirstId.current !== null && firstId !== null && firstId < previousFirstId.current) {
      // Older messages were prepended: keep the viewport on what it showed.
      element.scrollTop += element.scrollHeight - previousHeight.current
    } else if (stickToBottom.current) {
      element.scrollTop = element.scrollHeight
    }
    previousFirstId.current = firstId
    previousHeight.current = element.scrollHeight
  }, [messages])

  useLayoutEffect(() => {
    stickToBottom.current = true
    previousFirstId.current = null
    const element = listRef.current
    if (element) element.scrollTop = element.scrollHeight
  }, [channel.id])

  /* ------------------------------------------------------------- actions */
  const submit = () => {
    const body = draft.trim()
    if (!body || send.isPending) return
    setDraft('')
    stickToBottom.current = true
    send.mutate({ channelId: channel.id, body })
  }

  const composerRef = useRef<HTMLDivElement>(null)
  const onComposerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.metaKey || event.ctrlKey) return
    // The mention menu owns Enter while it is open.
    if (composerRef.current?.querySelector('[role="listbox"]')) return
    event.preventDefault()
    submit()
  }

  const startEdit = (message: ChatMessage) => {
    setEditing(message)
    setEditDraft(message.body)
  }
  const saveEdit = () => {
    if (!editing) return
    const body = editDraft.trim()
    if (!body || body === editing.body) {
      setEditing(null)
      return
    }
    edit.mutate(
      { messageId: editing.id, channelId: channel.id, body },
      { onSuccess: () => setEditing(null) },
    )
  }

  const isGroup = channel.kind === 'group'
  const isCreator = channel.created_by === currentUserId
  const canManage = isGroup && (isCreator || isAdmin)

  /* ------------------------------------------------------------- grouping */
  const groups = useMemo(() => groupByDay(messages), [messages])

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-3 border-b border-ink-200 px-4 py-2.5 dark:border-ink-800">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to conversations"
          className="-ml-1 rounded-md p-1.5 text-ink-600 hover:bg-ink-100 md:hidden dark:text-ink-300 dark:hover:bg-ink-800"
        >
          <ArrowLeft className="size-5" aria-hidden="true" />
        </button>
        {channel.kind === 'dm' ? (
          <Avatar user={channel.peer ?? null} size="md" />
        ) : (
          <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-ink-100 text-ink-600 dark:bg-ink-800 dark:text-ink-300">
            <Hash className="size-4" aria-hidden="true" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold">{channelTitle(channel)}</h2>
          <p className="truncate text-xs text-ink-500">
            {channel.kind === 'dm'
              ? channel.peer?.email
              : channel.members.map((member) => member.user?.name ?? `#${member.user_id}`).join(', ')}
          </p>
        </div>
        {isGroup ? (
          <div className="flex items-center gap-1">
            <div className="hidden -space-x-1.5 sm:flex" aria-hidden="true">
              {channel.members.slice(0, 5).map((member) => (
                <Avatar key={member.user_id} user={member.user ?? null} size="sm" className="ring-2 ring-white dark:ring-ink-950" />
              ))}
            </div>
            <Popover
              align="right"
              width="w-52"
              label="Group actions"
              trigger={({ toggle, open, id }) => (
                <button
                  type="button"
                  onClick={toggle}
                  aria-expanded={open}
                  aria-controls={id}
                  aria-label="Group actions"
                  className="rounded-md p-1.5 text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800"
                >
                  <MoreHorizontal className="size-5" aria-hidden="true" />
                </button>
              )}
            >
              {({ close }) => (
                <div className="space-y-0.5">
                  <MenuButton icon={<UserPlus className="size-4" />} onClick={() => { close(); setDialog('members') }}>
                    Add members
                  </MenuButton>
                  <MenuButton icon={<Pencil className="size-4" />} onClick={() => { close(); setDialog('rename') }}>
                    Rename group
                  </MenuButton>
                  <MenuButton
                    icon={<X className="size-4" />}
                    onClick={() => {
                      close()
                      removeMember.mutate({ channelId: channel.id, userId: currentUserId }, { onSuccess: onLeft })
                    }}
                  >
                    Leave group
                  </MenuButton>
                  {canManage ? (
                    <MenuButton danger icon={<Trash2 className="size-4" />} onClick={() => { close(); setDialog('delete') }}>
                      Delete group
                    </MenuButton>
                  ) : null}
                </div>
              )}
            </Popover>
          </div>
        ) : null}
      </header>

      <div ref={listRef} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {error ? (
          <ErrorBanner error={error} onRetry={() => void refetch()} />
        ) : isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : messages.length === 0 ? (
          <EmptyState
            title={channel.kind === 'dm' ? `Say hello to ${channel.peer?.name ?? 'your teammate'}` : `Welcome to ${channel.name}`}
            description="Messages you send here are visible to everyone in this conversation."
          />
        ) : (
          <>
            {hasNextPage ? (
              <div className="flex justify-center pb-3">
                <Button size="sm" variant="ghost" onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
                  {isFetchingNextPage ? 'Loading…' : 'Load earlier messages'}
                </Button>
              </div>
            ) : null}
            {groups.map((group) => (
              <section key={group.day} aria-label={formatDay(group.day)}>
                <div className="my-3 flex items-center gap-3">
                  <span className="h-px flex-1 bg-ink-200 dark:bg-ink-800" aria-hidden="true" />
                  <span className="text-[11px] font-medium tracking-wide text-ink-400 uppercase">{dayLabel(group.day)}</span>
                  <span className="h-px flex-1 bg-ink-200 dark:bg-ink-800" aria-hidden="true" />
                </div>
                {group.messages.map((message, index) => {
                  const previous = group.messages[index - 1]
                  const collapsed =
                    previous !== undefined &&
                    previous.author_id === message.author_id &&
                    !previous.deleted_at &&
                    minutesBetween(previous.created_at, message.created_at) < 5
                  const own = message.author_id === currentUserId
                  const isEditing = editing?.id === message.id
                  return (
                    <article
                      key={message.id}
                      className={cn('group relative flex gap-3 rounded-md px-2 py-0.5 hover:bg-ink-50 dark:hover:bg-ink-900', collapsed ? 'mt-0' : 'mt-2')}
                      aria-label={`${message.author?.name ?? 'Someone'} at ${formatMoment(message.created_at)}`}
                    >
                      <div className="w-8 shrink-0 pt-0.5">
                        {collapsed ? null : <Avatar user={message.author ?? null} size="md" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        {collapsed ? null : (
                          <div className="flex items-baseline gap-2">
                            <span className="text-sm font-semibold">{message.author?.name ?? 'Someone'}</span>
                            <time dateTime={message.created_at} className="text-[11px] text-ink-400">
                              {timeOf(message.created_at)}
                            </time>
                          </div>
                        )}
                        {isEditing ? (
                          <div className="mt-1 space-y-1.5">
                            <textarea
                              value={editDraft}
                              onChange={(event) => setEditDraft(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' && !event.shiftKey) {
                                  event.preventDefault()
                                  saveEdit()
                                }
                                if (event.key === 'Escape') setEditing(null)
                              }}
                              rows={2}
                              aria-label="Edit message"
                              className="w-full rounded-md border border-ink-300 bg-white px-2.5 py-1.5 text-sm dark:border-ink-700 dark:bg-ink-950"
                            />
                            <div className="flex gap-1.5">
                              <Button size="sm" variant="primary" onClick={saveEdit} disabled={edit.isPending}>
                                <Check className="size-3.5" aria-hidden="true" /> Save
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : message.deleted_at ? (
                          <p className="text-sm text-ink-400 italic">Message deleted</p>
                        ) : (
                          <p className={cn('text-sm break-words whitespace-pre-wrap text-ink-800 dark:text-ink-100', message.id < 0 && 'opacity-60')} dir="auto">
                            <CommentBody body={message.body} mentioned={mentioned} />
                            {message.edited_at ? <span className="ml-1 text-[11px] text-ink-400">(edited)</span> : null}
                          </p>
                        )}
                      </div>
                      {own && !message.deleted_at && !isEditing && message.id > 0 ? (
                        <div className="absolute top-0 right-2 hidden items-center gap-0.5 rounded-md border border-ink-200 bg-white p-0.5 shadow-sm group-hover:flex group-focus-within:flex dark:border-ink-700 dark:bg-ink-900">
                          <IconButton label="Edit message" onClick={() => startEdit(message)}>
                            <Pencil className="size-3.5" aria-hidden="true" />
                          </IconButton>
                          <IconButton label="Delete message" onClick={() => remove.mutate({ messageId: message.id, channelId: channel.id })}>
                            <Trash2 className="size-3.5" aria-hidden="true" />
                          </IconButton>
                        </div>
                      ) : null}
                    </article>
                  )
                })}
              </section>
            ))}
          </>
        )}
      </div>

      <div className="border-t border-ink-200 px-4 py-3 dark:border-ink-800">
        <ErrorBanner error={send.error ?? edit.error ?? remove.error} className="mb-2" />
        <div ref={composerRef} onKeyDown={onComposerKeyDown} className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <MentionTextarea
              value={draft}
              onChange={setDraft}
              members={rows}
              rows={2}
              placeholder={`Message ${channelTitle(channel)}`}
              onSubmit={submit}
              className="resize-none"
            />
          </div>
          <Button variant="primary" onClick={submit} disabled={!draft.trim() || send.isPending} aria-label="Send message">
            <Send className="size-4" aria-hidden="true" />
          </Button>
        </div>
        <p className="mt-1 text-[11px] text-ink-400">Enter to send, Shift+Enter for a new line, @ to mention.</p>
      </div>

      {dialog === 'rename' ? (
        <RenameDialog
          name={channel.name}
          pending={rename.isPending}
          error={rename.error}
          onClose={() => setDialog(null)}
          onSave={(name) => rename.mutate({ channelId: channel.id, name }, { onSuccess: () => setDialog(null) })}
        />
      ) : null}
      {dialog === 'members' ? (
        <AddMembersDialog
          slug={slug}
          channel={channel}
          pending={addMembers.isPending}
          error={addMembers.error}
          onClose={() => setDialog(null)}
          onAdd={(userIds) => addMembers.mutate({ channelId: channel.id, userIds }, { onSuccess: () => setDialog(null) })}
        />
      ) : null}
      {dialog === 'delete' ? (
        <Modal
          open
          onClose={() => setDialog(null)}
          title="Delete this group?"
          description="Every message in it is removed for everyone. This cannot be undone."
          footer={
            <>
              <Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
              <Button
                variant="danger"
                disabled={deleteChannel.isPending}
                onClick={() => deleteChannel.mutate(channel.id, { onSuccess: onLeft })}
              >
                {deleteChannel.isPending ? 'Deleting…' : 'Delete group'}
              </Button>
            </>
          }
        >
          <ErrorBanner error={deleteChannel.error} />
        </Modal>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ bits */

function MenuButton({
  icon,
  children,
  onClick,
  danger,
}: {
  icon: ReactNode
  children: ReactNode
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition',
        danger
          ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950'
          : 'text-ink-700 hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-ink-800',
      )}
    >
      <span aria-hidden="true">{icon}</span>
      {children}
    </button>
  )
}

function IconButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="rounded p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-800 dark:hover:bg-ink-800 dark:hover:text-ink-100"
    >
      {children}
    </button>
  )
}

function RenameDialog({
  name,
  pending,
  error,
  onClose,
  onSave,
}: {
  name: string
  pending: boolean
  error: unknown
  onClose: () => void
  onSave: (name: string) => void
}) {
  const [value, setValue] = useState(name)
  return (
    <Modal
      open
      onClose={onClose}
      title="Rename group"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!value.trim() || pending} onClick={() => onSave(value.trim())}>
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (value.trim()) onSave(value.trim())
        }}
        className="space-y-3"
      >
        <ErrorBanner error={error} />
        <Field label="Group name" htmlFor="chat-rename">
          <Input id="chat-rename" value={value} onChange={(event) => setValue(event.target.value)} maxLength={80} autoComplete="off" />
        </Field>
      </form>
    </Modal>
  )
}

function AddMembersDialog({
  slug,
  channel,
  pending,
  error,
  onClose,
  onAdd,
}: {
  slug: string
  channel: ChatChannel
  pending: boolean
  error: unknown
  onClose: () => void
  onAdd: (userIds: number[]) => void
}) {
  const roster = useRoster(slug)
  const [picked, setPicked] = useState<Set<number>>(() => new Set())
  const inChannel = new Set(channel.members.map((member) => member.user_id))
  const candidates = roster.members.filter((member) => member.user && !inChannel.has(member.user_id))
  return (
    <Modal
      open
      onClose={onClose}
      title="Add members"
      description={`Everyone you add can read the whole history of ${channel.name}.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={picked.size === 0 || pending} onClick={() => onAdd(Array.from(picked))}>
            {pending ? 'Adding…' : `Add ${picked.size || ''}`.trim()}
          </Button>
        </>
      }
    >
      <ErrorBanner error={error} className="mb-3" />
      {candidates.length === 0 ? (
        <p className="text-sm text-ink-500">Everyone in the workspace is already here.</p>
      ) : (
        <ul className="max-h-64 space-y-0.5 overflow-y-auto rounded-md border border-ink-200 p-1 dark:border-ink-800">
          {candidates.map((member) => (
            <li key={member.user_id}>
              <PopoverItem
                selected={picked.has(member.user_id)}
                onClick={() =>
                  setPicked((current) => {
                    const next = new Set(current)
                    if (next.has(member.user_id)) next.delete(member.user_id)
                    else next.add(member.user_id)
                    return next
                  })
                }
              >
                {member.user?.name}
              </PopoverItem>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}

/* --------------------------------------------------------------- helpers */

function groupByDay(messages: ChatMessage[]): Array<{ day: string; messages: ChatMessage[] }> {
  const out: Array<{ day: string; messages: ChatMessage[] }> = []
  for (const message of messages) {
    const day = localDay(message.created_at)
    const last = out[out.length - 1]
    if (last && last.day === day) last.messages.push(message)
    else out.push({ day, messages: [message] })
  }
  return out
}

function localDay(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value.slice(0, 10)
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 10)
}

function dayLabel(day: string): string {
  const today = localDay(new Date().toISOString())
  if (day === today) return 'Today'
  const yesterday = localDay(new Date(Date.now() - 86_400_000).toISOString())
  if (day === yesterday) return 'Yesterday'
  return formatDay(day)
}

function timeOf(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(date)
}

function minutesBetween(a: string, b: string): number {
  return Math.abs(new Date(b).getTime() - new Date(a).getTime()) / 60_000
}
